import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import * as Sentry from '@sentry/react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import type { PDFDocumentProxy } from 'pdfjs-dist';

import apiClient from 'clients/ApiClient';
import { InvoiceDetails } from '../clients/contracts';
import { UserState } from 'store/redux/Authentication';
import { ApplicationState } from 'store/redux';
import * as AuthenticationStore from 'store/redux/Authentication';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './InvoiceDetails.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function InvoiceDetailsPage(props: Props) {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const { id } = useParams();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [invoice, setInvoice] = useState<InvoiceDetails>();

  const customerId = props.userState?.UserInRole?.selectedCustomer?.customerId;

  const fetchInvoiceDetails = async (invoiceId: string) => {
    try {
      const client = apiClient.withAccessToken(props.userState?.AccessToken ?? '');
      setInvoice(await client.getInvoice(invoiceId, customerId ?? '0'));
    } catch (error) {
      Sentry.captureException(error);
      navigate('/service/invoices');
    }
  };

  const pdfUrl = useMemo(() => {
    if (!invoice?.documentBase64) return null;

    return `data:application/pdf;base64,${invoice?.documentBase64}`;
  }, [invoice]);

  const downloadInvoice = () => {
    if (!pdfUrl) return;

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${invoice?.description}.pdf`;
    link.click();
  };

  const backToList = () => {
    navigate('/service/invoices');
  };

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }: PDFDocumentProxy) => {
    setNumPages(nextNumPages);
  };

  useEffect(() => {
    if (!id) return;
    void fetchInvoiceDetails(id);
  }, [customerId, id]);

  return (
    <div className="row mt-4">
      <div className="col">
        <div className="afs-content">
          <div className="d-flex flex-row align-items-start justify-content-between">
            <Button className="btn-secondary" onClick={backToList} disabled={!pdfUrl}>
              {t('invoices.BACK_TO_LIST')}
            </Button>
            <h4>{invoice?.description}</h4>
            <Button className="btn-primary" onClick={downloadInvoice} disabled={!pdfUrl}>
              {t('invoices.DOWNLOAD_INVOICE')}
            </Button>
          </div>
          <div className="row">
            <div className="d-flex flex-column justify-content-center align-items-center">
              {numPages > 1 && (
                <div className="pages-control">
                  <Button disabled={pageNumber == 1} onClick={() => setPageNumber(pageNumber - 1)}>
                    {'<'}
                  </Button>
                  <span>
                    {pageNumber} / {numPages}
                  </span>
                  <Button
                    disabled={pageNumber == numPages}
                    onClick={() => setPageNumber(pageNumber + 1)}
                  >
                    {'>'}
                  </Button>
                </div>
              )}
              <Document
                file={pdfUrl}
                scale={1.5}
                onLoadSuccess={onDocumentLoadSuccess}
                noData={<Spinner className="spinner" />}
                loading={<Spinner className="spinner" />}
              >
                <Page pageNumber={pageNumber} />
              </Document>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type Props = {
  userState?: UserState;
};

export default connect((appState: ApplicationState) => ({ userState: appState.authentication }), {
  ...AuthenticationStore.actions,
})(InvoiceDetailsPage);
